import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Modal, Stepper, Button, Input, Textarea } from '../ui'
import Toggle from '../Toggle'
import PdfPages, { type PdfPageInfo } from '../pdf/PdfPages'
import { renderApiService } from '../../services/renderApiService'
import { esignApiService, type ESignCreateRequest, type ESignFieldKind } from '../../services/esignApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import {
  errorMessage, FIELD_LABELS, FIELD_SIZES, isPlanUpgradeError, isValidEmail, signerColor,
} from './esignShared'

const MAX_SIGNERS = 20
const KINDS: ESignFieldKind[] = ['signature', 'initials', 'date', 'name']

interface SignerRow { key: string; name: string; email: string }
interface PlacedField { key: string; signerKey: string; kind: ESignFieldKind; page: number; x: number; y: number; width: number; height: number }

let seq = 0
const nextKey = () => `k${++seq}`
const round2 = (n: number) => Math.round(n * 100) / 100
const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), Math.max(lo, hi))

interface SendForSignatureModalProps {
  renderJobId: string
  defaultTitle?: string
  onClose: () => void
  onSent?: (esignRequestId: string) => void
}

export default function SendForSignatureModal({ renderJobId, defaultTitle = '', onClose, onSent }: SendForSignatureModalProps) {
  const [step, setStep] = useState(0)
  const [title, setTitle] = useState(defaultTitle)
  const [message, setMessage] = useState('')
  const [signers, setSigners] = useState<SignerRow[]>(() => [{ key: nextKey(), name: '', email: '' }])
  const [sequential, setSequential] = useState(false)
  const [expiresInDays, setExpiresInDays] = useState('30')
  const [reminderDays, setReminderDays] = useState('3')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [pdf, setPdf] = useState<Blob | null>(null)
  const [pdfError, setPdfError] = useState('')
  const [activeSigner, setActiveSigner] = useState<string>('')
  const [activeKind, setActiveKind] = useState<ESignFieldKind>('signature')
  const [fields, setFields] = useState<PlacedField[]>([])

  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<{ message: string; upgrade: boolean } | null>(null)
  const [sentId, setSentId] = useState<string | null>(null)

  const drag = useRef<{ key: string; startX: number; startY: number; fx: number; fy: number; scale: number; maxX: number; maxY: number } | null>(null)

  useEffect(() => {
    if (step !== 1 || pdf) return
    renderApiService.downloadOutput(renderJobId)
      .then(setPdf)
      .catch(err => setPdfError(errorMessage(err, 'The rendered PDF could not be loaded.')))
  }, [step, pdf, renderJobId])

  useEffect(() => {
    if (!signers.some(s => s.key === activeSigner)) setActiveSigner(signers[0]?.key ?? '')
  }, [signers, activeSigner])

  useEffect(() => {
    function move(e: PointerEvent) {
      const d = drag.current
      if (!d) return
      const x = clamp(d.fx + (e.clientX - d.startX) / d.scale, 0, d.maxX)
      const y = clamp(d.fy + (e.clientY - d.startY) / d.scale, 0, d.maxY)
      setFields(fs => fs.map(f => (f.key === d.key ? { ...f, x, y } : f)))
    }
    function up() { drag.current = null }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [])

  const signerIndex = (key: string) => signers.findIndex(s => s.key === key)

  function updateSigner(key: string, patch: Partial<SignerRow>) {
    setSigners(ss => ss.map(s => (s.key === key ? { ...s, ...patch } : s)))
  }

  function moveSigner(i: number, dir: -1 | 1) {
    setSigners(ss => {
      const next = [...ss]
      const j = i + dir
      if (j < 0 || j >= next.length) return ss
      ;[next[i], next[j]] = [next[j]!, next[i]!]
      return next
    })
  }

  function removeSigner(key: string) {
    setSigners(ss => ss.filter(s => s.key !== key))
    setFields(fs => fs.filter(f => f.signerKey !== key))
  }

  function validateSigners(): boolean {
    const e: Record<string, string> = {}
    const seen = new Set<string>()
    signers.forEach(s => {
      const email = s.email.trim().toLowerCase()
      if (!s.name.trim()) e[`${s.key}:name`] = 'Name is required'
      if (!email) e[`${s.key}:email`] = 'Email is required'
      else if (!isValidEmail(email)) e[`${s.key}:email`] = 'Enter a valid email address'
      else if (seen.has(email)) e[`${s.key}:email`] = 'Each signer needs a different email'
      seen.add(email)
    })
    const exp = Number(expiresInDays)
    if (!Number.isInteger(exp) || exp < 1 || exp > 90) e.expires = 'Between 1 and 90 days'
    const rem = Number(reminderDays)
    if (!Number.isInteger(rem) || rem < 0 || rem > 30) e.reminders = 'Between 0 (off) and 30 days'
    if (signers.length === 0) e.signers = 'Add at least one signer'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function placeField(e: React.MouseEvent<HTMLDivElement>, info: PdfPageInfo) {
    if (e.target !== e.currentTarget || !activeSigner) return
    const rect = e.currentTarget.getBoundingClientRect()
    const size = FIELD_SIZES[activeKind]
    const px = (e.clientX - rect.left) / info.scale
    const py = (e.clientY - rect.top) / info.scale
    setFields(fs => [...fs, {
      key: nextKey(),
      signerKey: activeSigner,
      kind: activeKind,
      page: info.pageIndex,
      x: clamp(px - size.width / 2, 0, info.widthPt - size.width),
      y: clamp(py - size.height / 2, 0, info.heightPt - size.height),
      ...size,
    }])
  }

  function startDrag(e: React.PointerEvent, f: PlacedField, info: PdfPageInfo) {
    e.stopPropagation()
    e.preventDefault()
    drag.current = {
      key: f.key, startX: e.clientX, startY: e.clientY, fx: f.x, fy: f.y, scale: info.scale,
      maxX: info.widthPt - f.width, maxY: info.heightPt - f.height,
    }
  }

  function buildPayload(): ESignCreateRequest {
    const placed = fields
      .filter(f => signerIndex(f.signerKey) >= 0)
      .map(f => ({
        signerIndex: signerIndex(f.signerKey),
        kind: f.kind,
        page: f.page,
        x: round2(f.x), y: round2(f.y), width: round2(f.width), height: round2(f.height),
      }))
    return {
      renderJobId,
      title: title.trim() || undefined,
      message: message.trim() || undefined,
      signers: signers.map((s, i) => ({ email: s.email.trim(), name: s.name.trim(), order: i + 1 })),
      sequential,
      expiresInDays: Number(expiresInDays),
      reminderIntervalDays: Number(reminderDays),
      ...(placed.length ? { fields: placed } : {}),
    }
  }

  async function send() {
    setSending(true)
    setSendError(null)
    try {
      const { esignRequestId } = await esignApiService.create(buildPayload())
      setSentId(esignRequestId)
      onSent?.(esignRequestId)
    } catch (err) {
      setSendError(isPlanUpgradeError(err)
        ? { message: 'E-signatures are available on the Ultimate plan. Upgrade to send documents for signature.', upgrade: true }
        : { message: errorMessage(err, 'The signing request could not be sent.'), upgrade: false })
    } finally {
      setSending(false)
    }
  }

  const fieldsFor = (key: string) => fields.filter(f => f.signerKey === key)
  const withoutSignature = signers.filter(s => !fieldsFor(s.key).some(f => f.kind === 'signature'))

  if (sentId) {
    return (
      <Modal open onClose={onClose} title="Sent for signature" width={480}
        footer={<Button onClick={onClose}>Close</Button>}>
        <p style={{ fontSize: fontSize.base, color: colors.textBody, margin: 0 }}>
          Signing invitations are on their way{sequential ? ', starting with the first signer' : ''}.
          Track progress on the <Link to="/app/esign" onClick={onClose} style={{ color: colors.primary }}>E-signatures</Link> page.
        </p>
      </Modal>
    )
  }

  const footer = (
    <>
      <Button variant="ghost" onClick={step === 0 ? onClose : () => setStep(s => s - 1)} disabled={sending}>
        {step === 0 ? 'Cancel' : 'Back'}
      </Button>
      {step < 2
        ? <Button onClick={() => { if (step === 0 && !validateSigners()) return; setStep(s => s + 1) }}>Next</Button>
        : <Button onClick={send} loading={sending}>Send for signature</Button>}
    </>
  )

  return (
    <Modal open onClose={onClose} title="Send for signature" width={step === 1 ? 980 : 680} footer={footer}>
      <Stepper steps={['Signers', 'Fields', 'Review']} current={step} />

      {step === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <Input label="Title" aria-label="Title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Service Agreement" maxLength={200} />
          <Textarea label="Message (optional)" aria-label="Message" value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={2000} />

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm, gap: spacing.sm, flexWrap: 'wrap' }}>
              <span style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text }}>Signers</span>
              <Toggle checked={sequential} onChange={setSequential} label="Sign in order" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              {signers.map((s, i) => (
                <div key={s.key} data-testid="signer-row" style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <span style={{ width: 22, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fontSize.sm, fontWeight: 600, color: signerColor(i) }}>
                    {i + 1}
                  </span>
                  <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <Input aria-label={`Signer ${i + 1} name`} placeholder="Full name" value={s.name}
                      onChange={e => updateSigner(s.key, { name: e.target.value })} error={errors[`${s.key}:name`]} />
                  </div>
                  <div style={{ flex: '2 1 200px', minWidth: 0 }}>
                    <Input aria-label={`Signer ${i + 1} email`} placeholder="email@example.com" type="email" value={s.email}
                      onChange={e => updateSigner(s.key, { email: e.target.value })} error={errors[`${s.key}:email`]} />
                  </div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <IconBtn label={`Move signer ${i + 1} up`} disabled={i === 0} onClick={() => moveSigner(i, -1)}>↑</IconBtn>
                    <IconBtn label={`Move signer ${i + 1} down`} disabled={i === signers.length - 1} onClick={() => moveSigner(i, 1)}>↓</IconBtn>
                    <IconBtn label={`Remove signer ${i + 1}`} disabled={signers.length === 1} onClick={() => removeSigner(s.key)}>×</IconBtn>
                  </div>
                </div>
              ))}
            </div>
            {errors.signers && <div style={{ color: colors.error, fontSize: fontSize.xs, marginTop: 4 }}>{errors.signers}</div>}
            <Button variant="secondary" size="sm" style={{ marginTop: spacing.sm }} disabled={signers.length >= MAX_SIGNERS}
              onClick={() => setSigners(ss => [...ss, { key: nextKey(), name: '', email: '' }])}>
              Add signer
            </Button>
            {sequential && (
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.sm }}>
                Each signer is emailed after the one before them signs.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 180px' }}>
              <Input label="Expires after (days)" aria-label="Expires after (days)" type="number" min={1} max={90}
                value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)} error={errors.expires} />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <Input label="Remind every (days)" aria-label="Remind every (days)" type="number" min={0} max={30}
                value={reminderDays} onChange={e => setReminderDays(e.target.value)} error={errors.reminders}
                hint="0 turns reminders off" />
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, margin: `0 0 ${spacing.md}` }}>
            Choose a signer and a field type, then click the page to place it. Drag a field to move it.
            Fields are optional: a signer without a signature field is signed on the completion certificate.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.md }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Signer">
              {signers.map((s, i) => (
                <Chip key={s.key} active={activeSigner === s.key} color={signerColor(i)} onClick={() => setActiveSigner(s.key)}>
                  {s.name || s.email} ({fieldsFor(s.key).length})
                </Chip>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Field type">
              {KINDS.map(k => (
                <Chip key={k} active={activeKind === k} color={colors.text} onClick={() => setActiveKind(k)}>{FIELD_LABELS[k]}</Chip>
              ))}
            </div>
          </div>
          <div style={{ background: colors.background, borderRadius: radius.md, padding: spacing.md, overflowX: 'hidden' }}>
            {pdfError
              ? <div style={{ color: colors.error, fontSize: fontSize.sm }}>{pdfError}</div>
              : !pdf
                ? <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading document…</div>
                : (
                  <PdfPages file={pdf} onError={setPdfError} renderOverlay={info => (
                    <div data-testid={`place-page-${info.pageIndex}`} onClick={e => placeField(e, info)}
                      style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}>
                      {fields.filter(f => f.page === info.pageIndex).map(f => {
                        const idx = signerIndex(f.signerKey)
                        const c = signerColor(idx)
                        return (
                          <div key={f.key} data-testid="placed-field" onPointerDown={e => startDrag(e, f, info)} onClick={e => e.stopPropagation()}
                            style={{
                              position: 'absolute', left: f.x * info.scale, top: f.y * info.scale,
                              width: f.width * info.scale, height: f.height * info.scale, boxSizing: 'border-box',
                              border: `2px solid ${c}`, background: `${c}22`, borderRadius: 3, cursor: 'move',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none',
                              fontSize: Math.max(9, Math.min(12, f.height * info.scale * 0.45)), color: c, fontWeight: 600,
                              userSelect: 'none', overflow: 'visible',
                            }}>
                            {FIELD_LABELS[f.kind]}
                            <button type="button" aria-label={`Delete ${FIELD_LABELS[f.kind]} field`}
                              onPointerDown={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); setFields(fs => fs.filter(x => x.key !== f.key)) }}
                              style={{
                                position: 'absolute', top: -9, right: -9, width: 18, height: 18, borderRadius: '50%',
                                border: 'none', background: c, color: '#fff', fontSize: 12, lineHeight: '18px',
                                padding: 0, cursor: 'pointer',
                              }}>×</button>
                          </div>
                        )
                      })}
                    </div>
                  )} />
                )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, fontSize: fontSize.sm, color: colors.textBody }}>
          <ReviewRow label="Title">{title.trim() || '—'}</ReviewRow>
          {message.trim() && <ReviewRow label="Message"><span style={{ whiteSpace: 'pre-wrap' }}>{message.trim()}</span></ReviewRow>}
          <ReviewRow label="Signing order">{sequential ? 'In order, one at a time' : 'Everyone at once'}</ReviewRow>
          <ReviewRow label="Expires">{`After ${expiresInDays} day${expiresInDays === '1' ? '' : 's'}`}</ReviewRow>
          <ReviewRow label="Reminders">{reminderDays === '0' ? 'Off' : `Every ${reminderDays} day${reminderDays === '1' ? '' : 's'}`}</ReviewRow>
          <div>
            <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>Signers</div>
            {signers.map((s, i) => {
              const fs = fieldsFor(s.key)
              return (
                <div key={s.key} style={{ display: 'flex', gap: spacing.sm, alignItems: 'baseline', padding: '4px 0', flexWrap: 'wrap' }}>
                  <span style={{ color: signerColor(i), fontWeight: 600 }}>{i + 1}.</span>
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <span style={{ color: colors.textSecondary }}>{s.email}</span>
                  <span style={{ color: colors.textMuted }}>· {fs.length} field{fs.length === 1 ? '' : 's'}</span>
                </div>
              )
            })}
          </div>
          {withoutSignature.length > 0 && (
            <div style={{ background: colors.infoLight, border: `1px solid ${colors.infoBorder}`, color: colors.info, borderRadius: radius.sm, padding: spacing.sm }}>
              {withoutSignature.map(s => s.name).join(', ')} {withoutSignature.length === 1 ? 'has' : 'have'} no signature field and will be signed on the completion certificate.
            </div>
          )}
          {sendError && (
            <div role="alert" style={{ background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, color: colors.error, borderRadius: radius.sm, padding: spacing.sm }}>
              {sendError.message}
              {sendError.upgrade && <> <Link to="/app/billing" onClick={onClose} style={{ color: colors.error, fontWeight: 600 }}>View plans</Link></>}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function IconBtn({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}
      style={{
        width: 30, height: 34, border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface,
        color: colors.textSecondary, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, fontSize: fontSize.base,
      }}>
      {children}
    </button>
  )
}

function Chip({ active, color, onClick, children }: { active: boolean; color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: radius.full, fontSize: fontSize.sm, cursor: 'pointer',
        border: `1px solid ${active ? color : colors.border}`, background: active ? color : colors.surface,
        color: active ? '#fff' : colors.textSecondary, fontWeight: active ? 600 : 400, maxWidth: 240,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
      {children}
    </button>
  )
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: spacing.md }}>
      <span style={{ width: 120, flexShrink: 0, color: colors.textSecondary }}>{label}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  )
}
